import { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { 
  FileText, 
  Download, 
  Eye,
  CheckCircle2,
  Clock,
  AlertTriangle,
  TrendingUp,
  Share2,
  Sparkles,
  Loader2,
  Search,
  Mail
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LabResults = () => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedResult, setSelectedResult] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [interpreting, setInterpreting] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [sharing, setSharing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchResults();
  }, []);

  const fetchResults = async () => {
    try {
      const response = await axios.get(`${API}/results`);
      setResults(response.data);
    } catch (error) {
      console.error('Error fetching results:', error);
    } finally {
      setLoading(false);
    }
  };

  const viewResult = (result) => {
    setSelectedResult(result);
    setDialogOpen(true);
  };

  const handleDownloadPDF = async (resultId) => {
    setDownloading(true);
    toast.info('Preparing PDF download...');
    try {
      const response = await axios.get(`${API}/results/${resultId}/pdf`, {
        responseType: 'blob',
        timeout: 60000 // 60 second timeout for PDF generation
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `lab_result_${resultId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Result PDF downloaded successfully');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handleGetInterpretation = async (resultId) => {
    setInterpreting(true);
    try {
      const response = await axios.post(`${API}/results/${resultId}/interpret`);
      setSelectedResult(prev => ({
        ...prev,
        interpretation: response.data.interpretation
      }));
      // Update results list too
      setResults(prev => prev.map(r => 
        r.id === resultId ? {...r, interpretation: response.data.interpretation} : r
      ));
      toast.success('AI interpretation generated');
    } catch (error) {
      console.error('Error getting interpretation:', error);
      toast.error(error.response?.data?.detail || 'Failed to get AI interpretation');
    } finally {
      setInterpreting(false);
    }
  };

  const handleShareResult = async () => {
    if (!shareEmail || !selectedResult) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setSharing(true);
    try {
      await axios.post(`${API}/results/${selectedResult.id}/share`, {
        email: shareEmail
      });
      toast.success(`Result shared with ${shareEmail}`);
      setShareDialogOpen(false);
      setShareEmail('');
    } catch (error) {
      console.error('Error sharing result:', error);
      toast.error(error.response?.data?.detail || 'Failed to share result');
    } finally {
      setSharing(false);
    }
  };

  const filteredResults = results.filter(result => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      result.test_name?.toLowerCase().includes(query) ||
      result.id?.toLowerCase().includes(query)
    );
  });


  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="lab-results-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Lab Results</h1>
            <p className="text-muted-foreground mt-1">View and download your laboratory test results</p>
          </div>
        </div>

        {/* Search */}
        <div className="card-clinical">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by test name or ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="search-results-input"
            />
          </div>
        </div>

        {/* Results List */}
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="spinner" />
          </div>
        ) : filteredResults.length === 0 ? (
          <div className="text-center py-16 card-clinical">
            <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground">
              {results.length === 0 ? 'No results yet' : 'No results found'}
            </h3>
            <p className="text-muted-foreground mt-2">
              {results.length === 0 
                ? 'Your lab results will appear here once your tests are processed'
                : 'Try adjusting your search query'}
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredResults.map((result) => (
              <div 
                key={result.id} 
                className="card-clinical hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => viewResult(result)}
                data-testid={`result-card-${result.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-sky-100 dark:bg-sky-900/50 rounded-lg flex items-center justify-center">
                    <FileText className="w-6 h-6 text-sky-600 dark:text-sky-400" />
                  </div>
                  {result.verified_by ? (
                    <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Verified
                    </Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300">
                      <Clock className="w-3 h-3 mr-1" />
                      Pending Review
                    </Badge>
                  )}
                </div>
                <h3 className="font-semibold text-foreground mb-1">{result.test_name}</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  {new Date(result.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <span className="text-sm text-muted-foreground">
                    {result.parameters?.length || 0} parameters
                  </span>
                  <div className="flex gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-sky-600 dark:text-sky-400"
                      onClick={(e) => {
                        e.stopPropagation();
                        viewResult(result);
                      }}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-emerald-600 dark:text-emerald-400"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadPDF(result.id);
                      }}
                      data-testid={`download-result-${result.id}`}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Result Detail Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-foreground">
                <FileText className="w-5 h-5 text-sky-600 dark:text-sky-400" />
                {selectedResult?.test_name}
              </DialogTitle>
            </DialogHeader>
            
            {selectedResult && (
              <div className="space-y-6">
                {/* Meta Info */}
                <div className="flex flex-wrap gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Date:</span>
                    <span className="ml-2 font-medium text-foreground">
                      {new Date(selectedResult.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  {selectedResult.verified_by && (
                    <div>
                      <span className="text-muted-foreground">Verified:</span>
                      <span className="ml-2 font-medium text-emerald-600 dark:text-emerald-400">Yes</span>
                    </div>
                  )}
                </div>

                {/* Parameters Table */}
                {selectedResult.parameters && selectedResult.parameters.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-foreground mb-3">Test Parameters</h4>
                    <div className="border border-border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="text-left px-4 py-3 font-medium text-muted-foreground">Parameter</th>
                            <th className="text-left px-4 py-3 font-medium text-muted-foreground">Result</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {selectedResult.parameters.map((param, idx) => (
                            <tr key={idx} className="hover:bg-muted/30">
                              <td className="px-4 py-3 font-medium text-foreground">{param.name}</td>
                              <td className="px-4 py-3 text-foreground">
                                <span className="font-mono font-semibold">{param.value}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* AI Insights */}
                {selectedResult.ai_insights && (
                  <div className="p-4 bg-sky-50 dark:bg-sky-900/30 border border-sky-200 dark:border-sky-800 rounded-lg">
                    <h4 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-sky-600 dark:text-sky-400" />
                      AI Health Insights
                    </h4>
                    <p className="text-foreground/80">{selectedResult.ai_insights}</p>
                  </div>
                )}

                {/* Interpretation */}
                {selectedResult.interpretation && (
                  <div className="p-4 bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 rounded-lg">
                    <h4 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                      Clinical Interpretation
                    </h4>
                    <p className="text-foreground/80">{selectedResult.interpretation}</p>
                  </div>
                )}

                {/* Doctor Notes */}
                {selectedResult.doctor_notes && (
                  <div className="p-4 bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-800 rounded-lg">
                    <h4 className="font-semibold text-foreground mb-2">Doctor's Notes</h4>
                    <p className="text-foreground/80">{selectedResult.doctor_notes}</p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex flex-wrap gap-3 pt-4 border-t border-border">
                  <Button 
                    className="bg-sky-600 hover:bg-sky-700"
                    onClick={() => handleDownloadPDF(selectedResult.id)}
                    disabled={downloading}
                    data-testid="download-result-pdf-btn"
                  >
                    {downloading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Downloading...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        Download PDF
                      </>
                    )}
                  </Button>
                  {!selectedResult.interpretation && (
                    <Button 
                      variant="outline"
                      onClick={() => handleGetInterpretation(selectedResult.id)}
                      disabled={interpreting}
                      data-testid="get-interpretation-btn"
                    >
                      {interpreting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Analyzing...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Get AI Interpretation
                        </>
                      )}
                    </Button>
                  )}
                  <Button 
                    variant="outline"
                    onClick={() => setShareDialogOpen(true)}
                    data-testid="share-result-btn"
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    Share Report
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Share Dialog */}
        <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-sky-600" />
                Share Lab Result
              </DialogTitle>
              <DialogDescription>
                Enter the email address to share this result with
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Label>Recipient Email</Label>
              <Input
                type="email"
                placeholder="doctor@hospital.com"
                value={shareEmail}
                onChange={(e) => setShareEmail(e.target.value)}
                className="mt-2"
                data-testid="share-email-input"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShareDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleShareResult}
                disabled={sharing || !shareEmail}
                className="bg-sky-600 hover:bg-sky-700"
                data-testid="confirm-share-btn"
              >
                {sharing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Share2 className="w-4 h-4 mr-2" />
                    Share
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default LabResults;
